export type GuestOnlinePayments = {
  enabled?: boolean;
  configured?: boolean;
  guestEnabled?: boolean;
  available?: boolean;
  codEnabled?: boolean;
};

export function guestPayOnlineAvailable(flags?: GuestOnlinePayments | null): boolean {
  if (!flags) {
    return false;
  }
  if (typeof flags.available === 'boolean') {
    return flags.available;
  }
  return !!(flags.enabled && flags.configured && flags.guestEnabled);
}

export function guestCodAvailable(flags?: GuestOnlinePayments | null): boolean {
  return flags?.codEnabled !== false;
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  ONLINE_PAYMENT: 'Online',
  CASH_ON_DELIVERY: 'Cash on delivery',
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  CREDIT: 'Credit',
};

export type GuestPaidPresentation = 'full' | 'partial' | 'none';

/**
 * What the guest should see for money already taken. A balance on its own is not a payment,
 * and a PAID stamp with no online capture is not "paid online".
 */
export function guestPaidPresentation(input: {
  paymentStatus?: string | null;
  paymentMethod?: string | null;
  orderStatus?: string | null;
  onlinePaidAmount?: number | null;
  onlineBalanceDue?: number | null;
}): GuestPaidPresentation {
  const captured = (input.onlinePaidAmount ?? 0) > 0.004;
  const due = (input.onlineBalanceDue ?? 0) > 0.004;
  const status = (input.paymentStatus || '').toUpperCase();
  const orderStatus = (input.orderStatus || '').toUpperCase();
  const method = (input.paymentMethod || '').toUpperCase();
  const onlineMethod = method === 'ONLINE_PAYMENT';
  if (captured && due) {
    return 'partial';
  }
  if (status === 'PARTIALLY_PAID' && (due || captured)) {
    return 'partial';
  }
  const markedPaid = status === 'PAID' || orderStatus === 'PAYMENT_DONE';
  if (markedPaid && !due && (captured || !onlineMethod)) {
    return 'full';
  }
  return 'none';
}

export function guestPaymentMethodLabel(method?: string | null, orderType?: number): string {
  const key = String(method || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');
  if (!key) {
    return '';
  }
  if (key === 'CASH_ON_DELIVERY' && orderType === 3) {
    return 'Cash at pickup';
  }
  if (PAYMENT_METHOD_LABELS[key]) {
    return PAYMENT_METHOD_LABELS[key];
  }
  return key
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
}
